import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Trash } from '@phosphor-icons/react';
import { type SessionSummary } from '../../../services/chat.service';

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

interface SessionSidebarProps {
  sessions: SessionSummary[];
  activeSessionId: string | null;
  onSelect: (sessionId: string) => void;
  onNew: () => void;
  onDelete: (sessionId: string) => void;
  isOpen: boolean;
}

export function SessionSidebar({
  sessions,
  activeSessionId,
  onSelect,
  onNew,
  onDelete,
  isOpen,
}: SessionSidebarProps) {
  return (
    <motion.aside
      initial={false}
      animate={{ width: isOpen ? 240 : 0, opacity: isOpen ? 1 : 0 }}
      transition={{ duration: 0.3, ease: [0.23, 1, 0.32, 1] }}
      className="shrink-0 flex flex-col h-full bg-white/40 backdrop-blur-3xl shadow-[4px_0_24px_rgba(0,0,0,0.03)] z-20 overflow-hidden"
    >
      <div className="w-[240px] flex flex-col h-full shrink-0">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-5 relative">
          <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-widest font-outfit">History</span>
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            onClick={onNew}
            title="New chat"
            className="w-7 h-7 rounded-[10px] bg-orange-50/80 flex items-center justify-center text-orange-600 shadow-[0_2px_8px_rgba(249,115,22,0.12)] border border-orange-200/50 hover:bg-orange-100 transition-colors"
          >
            <Plus size={13} weight="bold" />
          </motion.button>
        </div>

        {/* Sessions list */}
        <div className="flex-1 overflow-y-auto py-2 px-3 space-y-1">
          <AnimatePresence>
            {sessions.length === 0 ? (
              <p className="text-[13px] text-slate-400 text-center mt-8 px-4">No conversations yet</p>
            ) : (
              sessions.map((s) => {
                const isActive = s.session_id === activeSessionId;
                return (
                  <div
                    key={s.session_id}
                    className="relative group px-3 py-3 cursor-pointer"
                    onClick={() => onSelect(s.session_id)}
                  >
                    {/* Active Indicator Background */}
                    {isActive && (
                      <motion.div
                        layoutId="activeSession"
                        className="absolute inset-0 bg-white rounded-xl shadow-[0_2px_12px_rgba(0,0,0,0.04)]"
                        initial={false}
                        transition={{ type: 'spring', stiffness: 350, damping: 30 }}
                      >
                        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-4 rounded-r-full bg-orange-500" />
                      </motion.div>
                    )}
                    
                    {/* Content */}
                    <div className="relative flex items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <p className={`text-[13px] leading-tight truncate transition-colors duration-200 ${isActive ? 'text-slate-800 font-semibold' : 'text-slate-600 font-medium group-hover:text-slate-800'}`}>
                          {s.title}
                        </p>
                        <p className={`text-[11px] mt-1 transition-colors duration-200 ${isActive ? 'text-slate-400' : 'text-slate-400/70 group-hover:text-slate-400'}`}>
                          {timeAgo(s.updated_at)}
                        </p>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onDelete(s.session_id);
                        }}
                        className="opacity-0 group-hover:opacity-100 p-1.5 rounded-lg hover:bg-red-50 hover:text-red-500 text-slate-300 transition-all"
                      >
                        <Trash size={12} weight={isActive ? "fill" : "regular"} />
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </AnimatePresence>
        </div>
      </div>
    </motion.aside>
  );
}
